/**
 * CSV export for Forecast bridge / band outcomes.
 */

const PLAN_LOGOS_DISCLAIMER =
  '# Plan logos (Basic/Premium/Enterprise) are an allocation view of total paid logos using the evolving plan mix — not independent plan-specific cohorts.';

function escapeCsv(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * @param {object[]} months — projection months
 * @param {(m: object) => { mrr: number, profit: number, logos: number, cash: number }} pickBand
 * @param {{ bandLabel?: string }} [opts]
 */
export function buildForecastCsv(months, pickBand, opts = {}) {
  const headers = [
    'Month',
    'Prev MRR',
    '+ New',
    '+ Expansion',
    '- Contraction',
    '- Churn',
    'Shock',
    'End MRR (ref)',
    'MRR',
    'Monthly profit',
    'Logos',
    'Basic logos',
    'Premium logos',
    'Enterprise logos',
    'Bank-linked logos',
    'Signups',
    'Marketing spend',
    'Variable COGS',
    'Plaid COGS',
    'Stripe COGS',
    'AI COGS',
    'AI pack revenue',
    'Conversion rate',
    'Bank-link rate',
    'Events',
    'Cash',
    'Band',
  ];
  const rows = (months || []).map((m) => {
    const band = pickBand(m);
    return [
      m.label,
      m.prevMrr,
      m.newMrr,
      m.expansion,
      m.contraction,
      m.churnMrr,
      m.shock,
      m.bridgeEndMrr ?? m.endMrr,
      band.mrr,
      band.profit,
      band.logos,
      band.logosByPlan?.basic,
      band.logosByPlan?.premium,
      band.logosByPlan?.enterprise,
      band.bankLinkedLogos,
      band.signups,
      band.marketingSpend,
      band.variableCogsMo,
      band.plaidCogsMo,
      band.stripeCogsMo,
      band.aiCogsMo,
      band.aiPackRevenue,
      band.convRate,
      band.bankLinkRate,
      Array.isArray(band.eventLabels) ? band.eventLabels.join('; ') : band.eventLabels,
      band.cash,
      opts.bandLabel || 'p50',
    ]
      .map(escapeCsv)
      .join(',');
  });
  return [PLAN_LOGOS_DISCLAIMER, headers.join(','), ...rows].join('\n');
}

export function downloadForecastCsv(csvText, filename = 'forecast-bridge.csv') {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * One-screen markdown summary for clipboard.
 */
export function buildForecastSummaryMarkdown({
  bandLabel,
  openingMrr,
  horizonMrr,
  logos,
  runway,
  monthsToProfit,
  cashRecovery,
  asOf,
}) {
  const profitLine =
    monthsToProfit == null
      ? 'Months to profit: never in horizon'
      : monthsToProfit === 0
        ? 'Months to profit: now'
        : `Months to profit: ${monthsToProfit}`;
  const recoveryLine =
    cashRecovery == null
      ? 'Cash recovery: never in horizon'
      : cashRecovery === 0
        ? 'Cash recovery: no drawdown'
        : `Cash recovery: ${cashRecovery} mo`;
  return [
    `# Forecast summary (${bandLabel || 'P50'})`,
    asOf ? `As of: ${asOf}` : null,
    '',
    `- Opening MRR: $${Number(openingMrr || 0).toFixed(2)}`,
    `- Horizon MRR: $${Number(horizonMrr || 0).toFixed(2)}`,
    `- Logos: ${logos ?? '—'}`,
    `- Cash runway: ${runway ?? '—'} mo`,
    `- ${profitLine}`,
    `- ${recoveryLine}`,
    '',
  ]
    .filter((line) => line != null)
    .join('\n');
}
